import { useEffect, useState } from 'react';

import type { ActionCardCallbacks } from '@littlebird/action-cards';

const FLOATING_LAYER_SELECTOR =
  '[data-radix-popper-content-wrapper], [data-radix-portal], [role="dialog"], [role="menu"], [role="listbox"]';

type FieldEditingCallbacks = Pick<
  ActionCardCallbacks,
  'onEdit' | 'onSaveEdit' | 'onCancelEdit'
>;

export const useFieldEditing = (
  editing: boolean,
  callbacks: FieldEditingCallbacks
) => {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [activatedFieldKey, setActivatedFieldKey] = useState<string>();

  useEffect(() => {
    if (!editing || !root) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Element)) return;
      if (root.contains(event.target)) return;
      const floatingLayer = event.target.closest(FLOATING_LAYER_SELECTOR);
      if (floatingLayer && !floatingLayer.contains(root)) return;
      callbacks.onSaveEdit();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') callbacks.onCancelEdit();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [editing, root, callbacks]);

  return {
    rootRef: setRoot,
    autoFocusFieldKey: editing ? activatedFieldKey : undefined,
    activateField: (fieldKey: string) => {
      setActivatedFieldKey(fieldKey);
      callbacks.onEdit();
    },
  };
};
